import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from './api';
import type {
  RbacResource,
  SessionInfo,
} from '@eotir/oidcbridge-shared/api-contracts';

export type RbacActionLetter = 'C' | 'R' | 'U' | 'D';

export type AuthState = 'loading' | 'unauthenticated' | 'forbidden' | 'error' | 'authenticated';

export interface Session {
  state: AuthState;
  user: SessionInfo | null;
  /** UI gating only — every endpoint re-checks server-side. */
  can: (resource: RbacResource, action: RbacActionLetter) => boolean;
  errorMessage?: string;
}

/**
 * Single source of truth for "who am I and what may I do". Every consumer
 * shares the ['me'] query cache, so the /api/me request happens once per
 * staleTime window regardless of how many components call this.
 */
export function useSession(): Session {
  const { data, isLoading, error } = useQuery({
    queryKey: ['me'],
    queryFn: ({ signal }) => api<SessionInfo>('/api/me', { signal }),
    staleTime: 5 * 60_000,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) return false;
      return failureCount < 2;
    },
  });

  if (isLoading) {
    return { state: 'loading', user: null, can: () => false };
  }
  if (error) {
    const apiErr = error instanceof ApiError ? error : null;
    if (apiErr?.status === 401) {
      return { state: 'unauthenticated', user: null, can: () => false };
    }
    if (apiErr?.status === 403) {
      // Valid session but the group has no permissions (e.g. the matrix
      // changed underneath an active session).
      return { state: 'forbidden', user: null, can: () => false };
    }
    return {
      state: 'error',
      user: null,
      can: () => false,
      errorMessage: (error as Error).message,
    };
  }
  const user = data ?? null;
  return {
    state: user ? 'authenticated' : 'loading',
    user,
    can: (resource, action) => (user?.permissions?.[resource] ?? '').includes(action),
  };
}
